import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { cn } from '@/lib/tailwind.utils'
import { appLink } from '@app/config/appLink'
import { ChevronDown } from 'lucide-react'
import { USE_CASES } from '../config/useCases.config'
import { useHomeNavigation } from '../hooks/useHomeNavigation'
import { accentBgMap } from '../types'
import { NavbarSkeleton } from './NavbarSkeleton'
import NavTriggerV2 from './SidebarToggleTrigger'
import { getActiveItemClassName, getNavLinkClassName } from '../utils/navItems.utils'

interface HomeV2NavbarProps {
  className?: string
  linkClassName?: string
}

const HomeV2Navbar = ({ className, linkClassName }: HomeV2NavbarProps) => {
  const { isLoading, navigate, pathname, logos, activeStates, showData, showDataUpload } =
    useHomeNavigation()

  const { isWelcomeActive, isFeaturesActive, isDataActive, isDataUploadActive, isUseCasesActive } =
    activeStates

  const getLinkClassName = (isActive: boolean) =>
    cn(getNavLinkClassName(isActive), navigationMenuTriggerStyle(), linkClassName)

  if (isLoading) return <NavbarSkeleton />

  return (
    <header
      className={cn(
        'flex h-14 w-full shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-14',
        className,
      )}
    >
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        {/* Left: panel toggle */}
        <NavTriggerV2 />

        {/* Center: navigation */}
        <div className="flex flex-1 justify-center">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuLink
                  data-active={isWelcomeActive || undefined}
                  className={getLinkClassName(isWelcomeActive)}
                  onClick={() => navigate(appLink.homeWelcome.path)}
                >
                  {appLink.homeWelcome.label}
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink
                  data-active={isFeaturesActive || undefined}
                  className={getLinkClassName(isFeaturesActive)}
                  onClick={() => navigate(appLink.homeFeatures.path)}
                >
                  {appLink.homeFeatures.label}
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-active={isUseCasesActive || undefined}
                    className={getLinkClassName(isUseCasesActive)}
                  >
                    {appLink.homeUseCases.label}
                    <ChevronDown className="h-4 w-4" />
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="center" className="w-80 p-2 bg-white">
                    {USE_CASES.map((uc) => {
                      const Icon = uc.iconComponent
                      const isActive = pathname.includes(`/use-cases/${uc.id}`)

                      return (
                        <DropdownMenuItem
                          key={uc.id}
                          onClick={() => navigate(`${appLink.homeUseCases.path}/${uc.id}/overview`)}
                          data-active={isActive || undefined}
                          className={cn(
                            'flex cursor-pointer items-center gap-3 px-3 py-3',
                            getActiveItemClassName(isActive),
                          )}
                        >
                          <div
                            className={cn(
                              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                              accentBgMap[uc.accentColor],
                            )}
                          >
                            <Icon />
                          </div>
                          <span className="text-xs font-medium text-foreground">{uc.title}</span>
                        </DropdownMenuItem>
                      )
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </NavigationMenuItem>

              {showData && (
                <NavigationMenuItem>
                  <NavigationMenuLink
                    data-active={isDataActive || undefined}
                    className={getLinkClassName(isDataActive)}
                    onClick={() => navigate(appLink.homeData.path)}
                  >
                    {appLink.homeData.label}
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )}

              {showDataUpload && (
                <NavigationMenuItem>
                  <NavigationMenuLink
                    data-active={isDataUploadActive || undefined}
                    className={getLinkClassName(isDataUploadActive)}
                    onClick={() => navigate(appLink.homeDataUpload.path)}
                  >
                    {appLink.homeDataUpload.label}
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* Right: logo */}
        <div className="flex min-w-[120px] items-center justify-end">
          <img src={logos.full} alt="Logo" className={cn('h-8 w-auto')} />
        </div>
      </div>
    </header>
  )
}

export default HomeV2Navbar
